import React, { useCallback } from "react";
import styled from "styled-components";
import {
  componentClasses,
  StyledCard,
  StyledDiv,
  StyledTypography,
} from "./SubstitutionManagementModalStyles";
import NoImageIcon from "../../assets/NoImage.png";
import TooltipComponent from "components/common/ToolTip";
import { SubsitutionManagementProductData } from "store/ai-cluster/AiClusterType";
import { useDispatch, useSelector } from "react-redux";
import { LoadingOverlayWithSpinner } from "components/common/LoadingOverlay";
import {
  selectAnalyticsEndDate,
  selectAnalyticsStartDate,
  selectDateRange,
  selectProductAnalyticsData,
  selectProductFetched,
  selectIsProductChildrenFetched,
  selectIsProductChildrenFetching,
  selectCategoryByProductId,
} from "store/product/ProductSelectors";
import {
  analyticsDataLoader,
  loadSingleProductAnalytics,
  resetSingleProducAnalyticView,
  loadCategoryByProductId,
} from "store/product/ProductActions";
import AppState from "store/AppState";
import AnalyticsOverlay from "components/overlays/AnalyticsOverlay";
import { selectIsAnalyticsEnabled } from "store/adapter-config/AdapterConfigSelectors";
import ProductActionIcons from "components/common/ProductActionIcons";
import ProductCategoryOverlay from "components/overlays/ProductCategoryOverlay";
import { refreshProductFromList } from "store/product-list/ProductListActions";
import { selectCurrentCatalogId } from "store/catalog/CatalogSelectors";
import {
  selectCurrentLocale,
  selectCurrentStoreId,
} from "store/store-list/StoreListSelectors";

const ImageContainer = styled.div`
  display: flex;
  justify-content: center; /* Horizontally centers the container */
  align-items: center;
  overflow: hidden;
  margin: auto;
  width: 155px;
  height: 157px;
  padding: 2px 0px;
  & .image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  & .baseImage {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const ImageContainerNonBase = styled.div`
  display: flex;
  justify-content: center; /* Horizontally centers the container */
  align-items: center;   
  overflow: hidden;
  margin: auto;
  width: 170px;
  height: 230px;
  padding-top: 4px;
  & .image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  }
`;

interface Props {
  product?: SubsitutionManagementProductData;
  code: string;
  productId: string;
  baseProductId?: string;
  height?: string;
  isColumn?: boolean;
  price?: Number;
  index?: Number;
  isSelected?: boolean;
  onTileClick?: (productId: string | null) => void;
  isAnalyticsDisplayed?: boolean;
  setIsAnalyticsDisplayed?: (value: boolean) => void;
  isCategoriesDisplayed?: boolean;
  setIsCategoriesDisplayed?: (value: boolean) => void;
}

const SubstitutionManagementProductItem: React.FC<Props> = (props) => {
  const {
    product,
    code,
    productId,
    isColumn,
    index,
    price,
    baseProductId,
    isSelected = false,
    onTileClick,
    isAnalyticsDisplayed,
    setIsAnalyticsDisplayed,
    isCategoriesDisplayed,
    setIsCategoriesDisplayed,
    ...otherProps
  } = props;
  const dispatch = useDispatch();
  const startDate = useSelector(selectAnalyticsStartDate);
  const endDate = useSelector(selectAnalyticsEndDate);
  const selectedDateRange = useSelector(selectDateRange);
  const catalogId = useSelector(selectCurrentCatalogId);
  const storeId = useSelector(selectCurrentStoreId);
  const localeCode = useSelector(selectCurrentLocale);
  const productAnalyticsId = product?.analyticsId ?? code;
  const showProductAnalytics = useSelector((state: AppState) =>
    selectProductAnalyticsData(state, productAnalyticsId),
  )?.showAnalytics;
  const loadSingleProduct = useSelector((state: AppState) =>
    selectProductAnalyticsData(state, productAnalyticsId),
  )?.loadAnalytics;
  const isAnalyticsEnabled = useSelector((state: AppState) =>
    selectIsAnalyticsEnabled(state),
  );
  const productDetails = useSelector((state: AppState) =>
    selectProductFetched(state, productId),
  );
  const isRefreshing = productDetails?.isRefreshing || false;
  const isProductChildrenFetched = useSelector(selectIsProductChildrenFetched);
  const isProductChildrenFetching = useSelector(
    selectIsProductChildrenFetching,
  );
  const categoryList = useSelector((state: AppState) =>
    selectCategoryByProductId(state, productId),
  );
  const handleClick = () => {
    if (isSelected) {
      onTileClick?.(null);
    } else {
      onTileClick?.(productId);
    }
  };
  const handleProductAnalytics = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    if (isCategoriesDisplayed) {
      setIsCategoriesDisplayed?.(false);
    }
    if (!showProductAnalytics) {
      setIsAnalyticsDisplayed?.(true);
      dispatch(analyticsDataLoader(productAnalyticsId));
      const fromDate = selectedDateRange.startDate;
      const toDate = selectedDateRange.endDate;
      if (fromDate !== startDate || toDate !== endDate) {
        const analyticsPayload = {
          startDate: fromDate,
          endDate: toDate,
          productCodes: [productAnalyticsId],
        };
        dispatch(loadSingleProductAnalytics(analyticsPayload));
      }
    } else {
      setIsAnalyticsDisplayed?.(false);
      dispatch(resetSingleProducAnalyticView(productAnalyticsId));
    }
  };

  const handleRefreshProduct = useCallback(
    (event) => {
      event.stopPropagation();
      let payload = {
        productId,
        catalogId,
        localeCode,
        storeId,
      };
      dispatch(refreshProductFromList(payload));
    },
    [catalogId, localeCode, storeId, dispatch, productId],
  );

  const productImage = product?.thumbnail;

  const renderProductImage = (
    isColumn,
    productImage,
    product,
    componentClasses,
  ) => {
    if (isColumn) {
      return (
        <ImageContainerNonBase>
          {productImage ? (
            <img
              className="image"
              src={productImage}
              alt={product?.name}
              draggable={false}
            />
          ) : (
            <StyledDiv className={componentClasses.emptyImageContainer}>
              <img
                className="image"
                src={NoImageIcon}
                alt={product?.name}
                draggable={false}
              />
            </StyledDiv>
          )}
        </ImageContainerNonBase>
      );
    } else {
      return (
        <ImageContainer>
          {productImage ? (
            <img
              className={!isColumn ? "baseImage" : "image "}
              src={productImage}
              alt={product?.name}
              draggable={false}
            />
          ) : (
            <StyledDiv className={componentClasses.emptyImageContainer}>
              <img
                className="image"
                src={NoImageIcon}
                alt={product?.name}
                draggable={false}
              />
            </StyledDiv>
          )}
        </ImageContainer>
      );
    }
  };

  const renderProductInfo = (isColumn, product, price, componentClasses) => {
    const calculatePrice = isColumn
      ? price
        ? `${"$" + price}`
        : ""
      : product?.price
        ? `${"$" + product?.price}`
        : "";
    return (
      <StyledDiv
        className={componentClasses.imageProductCodeDiv}
        isColumn={isColumn}
      >
        <TooltipComponent tooltipTitle={product?.name || ""} placement="bottom">
          <StyledTypography
            className={componentClasses.productName}
            isColumn={isColumn}
          >
            {product?.name || ""}
          </StyledTypography>
        </TooltipComponent>
        <TooltipComponent tooltipTitle={`${product?.code}`} placement="bottom">
          <StyledTypography
            className={componentClasses.productCodes}
            isColumn={isColumn}
          >
            {`${product?.code}`}
          </StyledTypography>
        </TooltipComponent>
        <TooltipComponent tooltipTitle={calculatePrice} placement="bottom">
          <StyledTypography
            className={componentClasses.productPrice}
            isColumn={isColumn}
          >
            {calculatePrice}
          </StyledTypography>
        </TooltipComponent>
      </StyledDiv>
    );
  };

  const handleCategories = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    if (!isCategoriesDisplayed) {
      if (showProductAnalytics) {
        setIsAnalyticsDisplayed?.(false);
        dispatch(resetSingleProducAnalyticView(productAnalyticsId));
      }
      setIsCategoriesDisplayed?.(true);
      const payload = {
        productIds: [productId],
      };
      dispatch(loadCategoryByProductId(payload));
    } else {
      setIsCategoriesDisplayed?.(false);
    }
  };

  return (
    <StyledDiv>
      <StyledDiv
        className={componentClasses.productCardContainer}
        isTileSelected={isSelected}
        {...(isColumn
          ? {
              onClick: handleClick,
            }
          : {})}
      >
        {isRefreshing && <LoadingOverlayWithSpinner />}
        {isColumn && (loadSingleProduct || showProductAnalytics) && (
          <AnalyticsOverlay
            productId={productId}
            showProductAnalytics={showProductAnalytics}
            loadSingleProductAnalytics={loadSingleProduct}
            isSubstituteOverlay={true}
          />
        )}
        {isColumn &&
          isCategoriesDisplayed &&
          (isProductChildrenFetching ||
            isProductChildrenFetched ||
            categoryList.length > 0) && (
            <ProductCategoryOverlay
              productId={productId}
              showOverlay={true}
              isSubstituteOverlay={true}
            />
          )}
        <StyledCard
          variant="outlined"
          className={
            !isColumn
              ? componentClasses.baseProductCard
              : componentClasses.productCard
          }
          {...otherProps}
        >
          <StyledDiv className={componentClasses.cardContentDiv}>
            {product?.typeCode && (
              <StyledTypography
                className={componentClasses.productType}
                isColumn={isColumn}
              >
                {product?.typeCode}
              </StyledTypography>
            )}
            <StyledTypography className={componentClasses.stockBase}>
              INV: {product?.stock}
              {isColumn && (
                <ProductActionIcons
                  product={productDetails || undefined}
                  isAnalyticsEnabled={!!isAnalyticsEnabled}
                  onRefresh={handleRefreshProduct}
                  onAnalytics={handleProductAnalytics}
                  onCategories={handleCategories}
                />
              )}
            </StyledTypography>
            {renderProductImage(
              isColumn,
              productImage,
              product,
              componentClasses,
            )}

            {renderProductInfo(isColumn, product, price, componentClasses)}
          </StyledDiv>
        </StyledCard>
      </StyledDiv>
    </StyledDiv>
  );
};

export default SubstitutionManagementProductItem;
